import React, { CSSProperties, ReactNode, useMemo } from "react";
import css from './BarsChart.module.scss';
import cn from "classnames";
import { sum } from 'radash';
import BarsChartItem, { Position } from './components/BarsChartItem/BarsChartItem';
import { formatNumber2d } from "@utils/format/number";
import { useElementSize } from "usehooks-ts";
import { Tooltip } from "@infobis/ui-styles-utils";
import { size } from "lodash";

export type Item<Custom extends Record<string, any> = {}> = {
	id: string
	name: string
	value: number
	color: string
} & Custom

export type BarsChartProps<ItemCustom extends Record<string, any> = {}> = {
	items: Item<ItemCustom>[]
	height: number
	onItemClick?: (item: Item<ItemCustom>) => void
	getTooltipText?: (item: Item<ItemCustom>) => ReactNode
	className?: string
	style?: CSSProperties
	gap?: number
	tooltipProps?: React.ComponentProps<typeof Tooltip>
	suppressTooltipStyles?: boolean
	compareFn?: (a: Item<ItemCustom>, b: Item<ItemCustom>) => number
	minWidth?: number
}

const BarsChart = <ItemCustom extends Record<string, any> = {}>(props: BarsChartProps<ItemCustom>) => {
	const {
		height,
		items,
		onItemClick = () => {},
		getTooltipText = (item) => formatNumber2d(item.value),
		className,
		style,
		gap = 0,
		tooltipProps,
		suppressTooltipStyles,
		compareFn,
		minWidth = 10
	} = props;

	const totalByRow = useMemo(() => sum(items.map(item => item.value)), [items])

	const [ref, { width }] = useElementSize()

	const getPosition = (index: number): Position => {
		if (index === 0) {
			return index === items.length - 1 ? 'single' : 'first';
		} else {
			return index === items.length - 1 ? 'last' : 'default';
		}
	}

	const sortedItems = useMemo(() => {
		return compareFn ? [...items].sort(compareFn) : items
	}, [items])

	const itemsCount = size(items)

	// константа, которую будем вычитать для предоставления минимальной ширины элемента
	const R_const = width - itemsCount * minWidth - (itemsCount - 1) * gap

	return (
		<div ref={ref} className={cn(css['chart'], className)} style={{ ...style, minHeight: height }}>
			{sortedItems.map((item, index) =>
				<BarsChartItem tooltipProps={tooltipProps}
							   width={item.value / totalByRow * R_const + minWidth}
							   suppressTooltipStyles={suppressTooltipStyles}
							   key={item.id}
							   item={item}
							   position={getPosition(index)}
							   height={height}
							   getTooltipText={getTooltipText}
							   onItemClick={onItemClick} />
			)}
		</div>
	)
}

export default BarsChart;